class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.cart_icon = document.querySelector('#cart-icon-bubble-short')?'cart-icon-bubble-short':'cart-icon-bubble';
    this.cart_icon_mobile = document.querySelector('#cart-icon-bubble--mobile-short')?'cart-icon-bubble--mobile-short':'cart-icon-bubble--mobile';
    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.setHeaderCartIconAccessibility();
    this.updRecom = true;
    this.opened = false;

    // Whenever the drawer content is re-rendered (add to cart, quantity change, remove),
    // make sure every "Complete the Collection" box loads its own products.
    this.recomTimer = null;
    this.setupCollapseMemory();
    new MutationObserver(() => {
      // Runs before the browser paints, so a box the user closed never flashes open after a re-render.
      this.applyCollapseState();
      clearTimeout(this.recomTimer);
      this.recomTimer = setTimeout(() => this.loadAllRecommendations(), 100);
    }).observe(this, { childList: true, subtree: true });
    setTimeout(() => this.loadAllRecommendations(), 0);
  }
  setUpdRecom(){
    this.updRecom = true;
  }

  // Remembers which "Complete the Collection" boxes the user closed, per product.
  // The drawer HTML is re-rendered on every add to cart and always comes back with the box open,
  // so the saved state is re-applied after each render. A box stays closed until the user opens it again.
  // New products still open by default. Saved in sessionStorage so it also survives page changes.
  setupCollapseMemory(){
    this.dcompStorageKey = 'dcomp-closed-boxes';
    this.dcompClosed = new Set();
    try {
      const saved = JSON.parse(sessionStorage.getItem(this.dcompStorageKey) || '[]');
      if (Array.isArray(saved)) saved.forEach((id) => this.dcompClosed.add(id));
    } catch (error) {}

    // The toggle event does not bubble, so listen in the capture phase.
    this.addEventListener('toggle', (event) => {
      const details = event.target;
      if (!details.classList || !details.classList.contains('dcomp-inline__details')) return;
      const box = details.closest('.dcomp-inline');
      const id = box && box.dataset.productIdJs;
      if (!id) return;

      if (details.open) {
        this.dcompClosed.delete(id);
      } else {
        this.dcompClosed.add(id);
      }
      try {
        sessionStorage.setItem(this.dcompStorageKey, JSON.stringify(Array.from(this.dcompClosed)));
      } catch (error) {}
    }, true);

    this.applyCollapseState();
  }

  applyCollapseState(){
    if (!this.dcompClosed || this.dcompClosed.size === 0) return;
    this.querySelectorAll('.dcomp-inline').forEach((box) => {
      const details = box.querySelector('.dcomp-inline__details');
      if (details && details.open && this.dcompClosed.has(box.dataset.productIdJs)) {
        details.open = false;
      }
    });
  }
  setHeaderCartIconAccessibility() {
    document.querySelectorAll(`[id*="cart-icon-bubble"]`).forEach((cartLink) => {
      cartLink.setAttribute('role', 'button');
      cartLink.setAttribute('aria-haspopup', 'dialog');
      cartLink.addEventListener('click', (event) => {
        event.preventDefault();
        this.open(cartLink);
      });
      cartLink.addEventListener('keydown', (event) => {
        if (event.code.toUpperCase() === 'SPACE') {
          event.preventDefault();
          this.open(cartLink);
        }
      });
    })
  }

  open(triggeredBy) {
    this.opened = true;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    setTimeout(() => {
      this.classList.add('active');
      this.classList.add('animate');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    if(document.querySelectorAll('.popup-modal.active').length == 0){
      document.body.style.paddingRight = this.getScrollbarWidth() + 'px';
      document.body.classList.add('overflow-hidden');
    }

    this.openComplementary(1500);
    this.loadAllRecommendations();
  }

  // The theme loads complementary products lazily (IntersectionObserver), which does not fire
  // for every hidden box on desktop. Fetch the products for each empty box directly.
  loadAllRecommendations(){
    this.querySelectorAll('.dcomp-inline complementary-products').forEach((element) => {
      if (element.children.length > 0 || element.dataset.dcompRequested) return;
      if (!element.dataset.url || !element.dataset.productId) return;
      element.dataset.dcompRequested = 'true';
      this.fetchRecommendations(element);
    });
  }

  fetchRecommendations(element){
    const url = `${element.dataset.url}&product_id=${element.dataset.productId}&section_id=${element.dataset.sectionId}`;
    fetch(url)
      .then((response) => response.text())
      .then((text) => {
        if (!element.isConnected || element.children.length > 0) return;
        const doc = new DOMParser().parseFromString(text, 'text/html');
        const source = doc.querySelector('complementary-products');
        if (source && source.innerHTML.trim().length) {
          element.innerHTML = source.innerHTML;
          this.updateRecommendations(element);
        }
      })
      .catch((error) => {
        delete element.dataset.dcompRequested;
        console.error(error);
      });
  }

  // Desktop side panel removed. Recommendations now render inline below each cart item.
  openComplementary(){
    return false;
  }
  clearAdditionalProducts(){
    return false;
  }

  // Called when complementary products finish loading. Shows the inline box on all screen sizes.
  updateRecommendations(complementaryProducts){
    const box = complementaryProducts.closest('.dcomp-inline');
    if (!box) {
      return false;
    }
    if (complementaryProducts.children.length > 0) {
      box.classList.remove('hide');
      this.setupSlider(complementaryProducts);
      this.dedupeRecommendations();
      setTimeout(() => this.matchParentVariant(box), 0);

      // Re-run setup if the product cards get replaced later (async render or variant change).
      if (!complementaryProducts.dataset.sliderObserved) {
        complementaryProducts.dataset.sliderObserved = 'true';
        new MutationObserver(() => this.setupSlider(complementaryProducts))
          .observe(complementaryProducts, { childList: true });
      }
    }
    return false;
  }

  // Several cart items can recommend the same products (for example a pillow cover and a fitted sheet
  // from the same collection). Show each recommended product only once, in the first box from the top.
  // A box whose products were all shown above is hidden completely.
  dedupeRecommendations(){
    const seen = new Set();

    this.querySelectorAll('.dcomp-inline').forEach((box) => {
      const complementaryProducts = box.querySelector('complementary-products');
      if (!complementaryProducts || complementaryProducts.children.length === 0) return;

      let visibleCount = 0;
      complementaryProducts.querySelectorAll('.product-card-small').forEach((card) => {
        const key = this.getCardProductKey(card);
        if (key && seen.has(key)) {
          card.style.setProperty('display', 'none', 'important');
        } else {
          card.style.removeProperty('display');
          if (key) seen.add(key);
          visibleCount += 1;
        }
      });

      box.classList.toggle('hide', visibleCount === 0);

      // Hidden duplicates change the slide count, so reset the slider (this also rebuilds the dots).
      const track = box.querySelector('.dcomp-track');
      if (track && track._dcompSlider) track._dcompSlider.goTo(0, false);
    });
  }

  // Preselects each recommended product in the same options as the cart item above it
  // (for example Size: Queen, Color: Gold). Options are matched by name, so it works whatever order they are in.
  // If no variant shares any of those values, the card keeps its default variant.
  // Runs once per card, so a choice the customer makes later in the dropdown is never overwritten.
  matchParentVariant(box){
    const parentOptions = this.getParentOptions(box);
    if (Object.keys(parentOptions).length === 0) return;

    box.querySelectorAll('card-variant-selects').forEach((picker) => {
      if (picker.dataset.dcompMatched) return;
      picker.dataset.dcompMatched = 'true';

      const variants = this.getPickerVariants(picker);
      const selects = Array.from(picker.querySelectorAll('select'));
      if (!variants.length || !selects.length) return;

      const optionNames = selects.map((select) => this.getSelectOptionName(select));

      // Score every variant by how many of its options match the cart item. Available variants win ties.
      let best = null;
      let bestScore = 0;
      variants.forEach((variant) => {
        let score = 0;
        optionNames.forEach((name, index) => {
          const wanted = parentOptions[name];
          const value = variant[`option${index + 1}`];
          if (wanted && value && this.normalizeOption(value) === wanted) score += 1;
        });
        if (score === 0) return;
        if (score > bestScore || (score === bestScore && variant.available && !(best && best.available))) {
          best = variant;
          bestScore = score;
        }
      });
      if (!best) return;

      let changedSelect = null;
      selects.forEach((select, index) => {
        const value = best[`option${index + 1}`];
        if (value == null) return;
        const option = Array.from(select.options).find((item) =>
          this.normalizeOption(item.value) === this.normalizeOption(value) ||
          this.normalizeOption(item.textContent) === this.normalizeOption(value)
        );
        if (option && !option.selected) {
          select.value = option.value;
          changedSelect = select;
        }
      });

      // Trigger the theme's variant update directly on the picker, without bubbling.
      // A bubbling change event would reach cart-drawer-items, which treats any change as a
      // quantity update and sends an invalid /cart/change request (400 Bad Request).
      // The picker's handler reads all dropdowns itself, so it does not need the select as target.
      if (changedSelect) {
        picker.dispatchEvent(new Event('change', { bubbles: false }));
      }
    });
  }

  // Reads the options shown under the cart item (for example "Size: Queen") into { size: 'queen' }.
  getParentOptions(box){
    const options = {};
    const item = box.previousElementSibling;
    if (!item || !item.classList.contains('drawer__cart-item')) return options;

    item.querySelectorAll('.product-option01').forEach((row) => {
      const name = row.querySelector('dt');
      const value = row.querySelector('dd');
      if (!name || !value) return;
      options[this.normalizeOptionName(name.textContent)] = this.normalizeOption(value.textContent);
    });
    return options;
  }

  getPickerVariants(picker){
    try {
      if (typeof picker.getVariantData === 'function') return picker.getVariantData() || [];
      const script = picker.querySelector('[type="application/json"]');
      return script ? JSON.parse(script.textContent) : [];
    } catch (error) {
      return [];
    }
  }

  // Finds the option name of a dropdown from its label, falling back to the select name like options[Size].
  getSelectOptionName(select){
    let label = select.id ? select.closest('card-variant-selects').querySelector(`label[for="${select.id}"]`) : null;
    if (!label) {
      const field = select.closest('.field, .select, .product-form__input, fieldset, div');
      label = field ? field.querySelector('label, legend') : null;
    }
    let text = label ? label.textContent : '';
    if (!text && select.name) {
      const match = select.name.match(/\[([^\]]+)\]/);
      text = match ? match[1] : select.name;
    }
    return this.normalizeOptionName(text);
  }

  normalizeOptionName(text){
    const name = String(text || '').replace(':', '').trim().toLowerCase();
    return name === 'colour' ? 'color' : name;
  }

  normalizeOption(text){
    return String(text || '').replace(/\s+/g, ' ').trim().toLowerCase();
  }

  // Uses the product handle from the card link, so different variants of one product count as the same product.
  getCardProductKey(card){
    const link = card.querySelector('a[href*="/products/"]');
    if (!link) return null;
    const path = new URL(link.getAttribute('href'), window.location.origin).pathname;
    return path.split('/products/')[1] || path;
  }

  // Finds the element that directly holds the product cards and turns it into the slider track.
  // Cards may sit inside a wrapper, so walk down until we reach the element whose children are the cards.
  setupSlider(complementaryProducts){
    const countCards = (element) =>
      Array.from(element.children).filter((child) => child.querySelector('img')).length;

    let track = complementaryProducts;
    while (track.children.length === 1 && countCards(track.firstElementChild) > 1) {
      track = track.firstElementChild;
    }

    complementaryProducts.querySelectorAll('.dcomp-track').forEach((element) => {
      if (element !== track) element.classList.remove('dcomp-track');
    });
    track.classList.add('dcomp-track');

    if (!track._dcompSlider) {
      track._dcompSlider = new DcompSlider(track);
    }
    track._dcompSlider.refresh();
  }

  close() {
    this.classList.remove('animate');
    setTimeout(() => {
      this.classList.remove('active');
      if(document.querySelectorAll('.popup-modal.active').length == 0){
        document.body.classList.remove('overflow-hidden');
        document.body.style.paddingRight = '';
      }
    }, 500);
    removeTrapFocus(this.activeElement);
    this.opened = false;
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') && this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.updRecom = true;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
      if(sectionElement){
        sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
      }
      if(section.id == 'cart-free-delivery' && document.querySelector('#product-page-free-delivery')){
        document.querySelector('#product-page-free-delivery').innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
      }
      if(section.id == this.cart_icon && document.querySelector('.footer-sticky-nav__item__icon .cart-count-bubble')){
        const parser = new DOMParser();
        const bubble = parser.parseFromString(this.getSectionInnerHTML(parsedState.sections[section.id], section.selector), 'text/html');
        var ib = bubble.querySelector('.cart-count-bubble')?bubble.querySelector('.cart-count-bubble').innerHTML : '' ;
        document.querySelector('.footer-sticky-nav__item__icon .cart-count-bubble').innerHTML = ib;
      }
    });

    setTimeout(() => {
      this.open();
    });
  }
  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: this.cart_icon,
      },
      {
        id: this.cart_icon_mobile
      },
      {
        id: 'cart-free-delivery'
      }
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
  getScrollbarWidth() {
    return window.innerWidth - document.documentElement.clientWidth;
  }
}
customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    this.cart_icon = document.querySelector('#cart-icon-bubble-short')?'cart-icon-bubble-short':'cart-icon-bubble';
    this.cart_icon_mobile = document.querySelector('#cart-icon-bubble--mobile-short')?'cart-icon-bubble--mobile-short':'cart-icon-bubble--mobile';
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: this.cart_icon,
        section: this.cart_icon,
        selector: '.shopify-section',
      },
      {
        id: this.cart_icon_mobile,
        section: this.cart_icon_mobile,
        selector: '.shopify-section'
      }
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);


/*
 * JavaScript slider for the "Complete the Collection" box.
 * - One slide at a time, next slide peeks in, pagination dots below (no arrows).
 * - Drag is fully handled in JavaScript (no native scroll or CSS scroll-snap).
 * - Uses Pointer Events where available, and falls back to Touch + Mouse events
 *   for older browsers (for example iOS Safari 12 and below).
 * - Vertical page scroll keeps working; only a horizontal swipe moves the slider.
 * - Trackpad horizontal swipe on desktop moves one slide.
 * - Taps on dropdowns and the Add to Cart button work normally.
 * - Dots stay in sync with drag, trackpad and keyboard, and clicking a dot jumps to that slide.
 */
class DcompSlider {
  constructor(track) {
    this.track = track;
    this.index = 0;
    this.offset = 0;
    this.isDown = false;
    this.direction = null;
    this.pointerId = null;
    this.suppressClick = false;
    this.wheelLocked = false;

    this.bindEvents();

    track.addEventListener('click', (event) => {
      if (this.suppressClick) {
        event.preventDefault();
        event.stopPropagation();
        this.suppressClick = false;
      }
    }, true);
    track.addEventListener('dragstart', (event) => event.preventDefault());
    track.addEventListener('wheel', (event) => this.onWheel(event), { passive: false });

    // Keyboard focus on a hidden slide can scroll the track natively; keep it at 0 and move the slider instead.
    track.addEventListener('scroll', () => { track.scrollLeft = 0; });
    track.addEventListener('focusin', (event) => {
      const slides = this.getSlides();
      const slide = slides.find((item) => item.contains(event.target));
      if (slide) this.goTo(slides.indexOf(slide));
    });

    if ('ResizeObserver' in window) {
      new ResizeObserver(() => this.goTo(this.index, false)).observe(track);
    } else {
      window.addEventListener('resize', () => this.goTo(this.index, false));
    }

    this.refresh();
  }

  bindEvents() {
    const track = this.track;

    if (window.PointerEvent) {
      track.addEventListener('pointerdown', (event) => {
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        this.start(event.clientX, event.clientY, event.target, event.pointerId);
      });
      track.addEventListener('pointermove', (event) => {
        // Mouse released outside the track before the drag started.
        if (event.pointerType === 'mouse' && event.buttons === 0 && this.isDown && this.direction === null) {
          this.isDown = false;
          return;
        }
        if (this.move(event.clientX, event.clientY)) event.preventDefault();
      });
      track.addEventListener('pointerup', (event) => this.end(event.clientX));
      track.addEventListener('pointercancel', () => this.end(this.lastX));
      return;
    }

    // Fallback: Touch events
    track.addEventListener('touchstart', (event) => {
      const touch = event.touches[0];
      this.start(touch.clientX, touch.clientY, event.target, null);
    }, { passive: true });
    track.addEventListener('touchmove', (event) => {
      const touch = event.touches[0];
      if (this.move(touch.clientX, touch.clientY)) event.preventDefault();
    }, { passive: false });
    track.addEventListener('touchend', (event) => {
      const touch = event.changedTouches[0];
      this.end(touch ? touch.clientX : this.lastX);
    });
    track.addEventListener('touchcancel', () => this.end(this.lastX));

    // Fallback: Mouse events
    track.addEventListener('mousedown', (event) => {
      if (event.button !== 0) return;
      this.start(event.clientX, event.clientY, event.target, null);
      const onMouseMove = (moveEvent) => {
        if (this.move(moveEvent.clientX, moveEvent.clientY)) moveEvent.preventDefault();
      };
      const onMouseUp = (upEvent) => {
        document.removeEventListener('mousemove', onMouseMove);
        document.removeEventListener('mouseup', onMouseUp);
        this.end(upEvent.clientX);
      };
      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
    });
  }

  getSlides() {
    return Array.from(this.track.children).filter((item) => getComputedStyle(item).display !== 'none');
  }

  getStep() {
    const slides = this.getSlides();
    if (slides.length < 2) return slides[0] ? slides[0].offsetWidth : 0;
    return slides[1].offsetLeft - slides[0].offsetLeft;
  }

  // How far the track can move before the last slide reaches the right edge.
  // Keeps the last slide aligned to the right instead of leaving empty space after it.
  getMaxOffset() {
    const slides = this.getSlides();
    if (slides.length < 2) return 0;
    const style = getComputedStyle(this.track);
    const innerWidth = this.track.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight);
    const last = slides[slides.length - 1];
    const contentWidth = last.offsetLeft - slides[0].offsetLeft + last.offsetWidth;
    return Math.max(contentWidth - innerWidth, 0);
  }

  refresh() {
    this.goTo(this.index, false);
  }

  setOffset(offset, animate) {
    this.offset = offset;
    const transition = animate ? 'transform .4s cubic-bezier(.25, .8, .25, 1)' : 'none';
    const transform = `translate3d(${offset}px, 0, 0)`;
    this.getSlides().forEach((slide) => {
      slide.style.webkitTransition = animate ? '-webkit-transform .4s cubic-bezier(.25, .8, .25, 1)' : 'none';
      slide.style.transition = transition;
      slide.style.webkitTransform = transform;
      slide.style.transform = transform;
    });
  }

  goTo(index, animate = true) {
    // With only one slide there is nothing to peek at, so let it take the full width.
    // Toggled before measuring, so the step and offset below use the right slide width.
    this.track.classList.toggle('is-single', this.getSlides().length < 2);

    const maxIndex = Math.max(this.getSlides().length - 1, 0);
    this.index = Math.min(Math.max(index, 0), maxIndex);
    this.setOffset(-Math.min(this.index * this.getStep(), this.getMaxOffset()), animate);
    this.updateDots();
  }

  // Builds the pagination dots below the slider and marks the active one.
  // Dots are rebuilt only when the number of visible slides changes.
  updateDots() {
    const body = this.track.closest('.dcomp-inline__body') || this.track.parentElement;
    if (!body) return;

    let dots = body.querySelector('.dcomp-dots');
    if (!dots) {
      dots = document.createElement('div');
      dots.className = 'dcomp-dots';
      dots.setAttribute('aria-label', 'Slides');
      body.appendChild(dots);

      dots.addEventListener('click', (event) => {
        const dot = event.target.closest('.dcomp-dot');
        if (!dot || !dots._slider) return;
        event.preventDefault();
        dots._slider.goTo(parseInt(dot.dataset.index, 10));
      });
    }

    // The track can be rebuilt after a re-render, so always point the dots at the current slider.
    dots._slider = this;

    const total = this.getSlides().length;
    dots.hidden = total < 2;

    if (dots.children.length !== total) {
      dots.innerHTML = '';
      for (let i = 0; i < total; i++) {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'dcomp-dot';
        dot.dataset.index = i;
        dot.setAttribute('aria-label', 'Go to slide ' + (i + 1));
        dots.appendChild(dot);
      }
    }

    Array.from(dots.children).forEach((dot, i) => {
      const active = i === this.index;
      dot.classList.toggle('is-active', active);
      if (active) {
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.removeAttribute('aria-current');
      }
    });
  }

  start(x, y, target, pointerId) {
    if (target.closest('select, input, textarea, button')) return;
    if (this.getSlides().length < 2) return;

    this.isDown = true;
    this.direction = null;
    this.pointerId = pointerId;
    this.startX = x;
    this.startY = y;
    this.startOffset = this.offset;
    this.lastX = x;
    this.lastTime = Date.now();
    this.velocity = 0;
  }

  // Returns true when the slider is handling the gesture (so the caller can block page scroll).
  move(x, y) {
    if (!this.isDown) return false;

    const deltaX = x - this.startX;
    const deltaY = y - this.startY;

    if (this.direction === null) {
      if (Math.abs(deltaX) < 6 && Math.abs(deltaY) < 6) return false;
      this.direction = Math.abs(deltaX) > Math.abs(deltaY) ? 'x' : 'y';
      if (this.direction === 'y') {
        this.isDown = false;
        return false;
      }
      this.track.classList.add('is-dragging');
      if (this.pointerId !== null && this.track.setPointerCapture) {
        try { this.track.setPointerCapture(this.pointerId); } catch (error) {}
      }
    }

    const now = Date.now();
    const elapsed = now - this.lastTime;
    if (elapsed > 0) this.velocity = (x - this.lastX) / elapsed;
    this.lastX = x;
    this.lastTime = now;

    // Resistance at the first and last slide.
    const minOffset = -this.getMaxOffset();
    let offset = this.startOffset + deltaX;
    if (offset > 0) offset = offset / 3;
    if (offset < minOffset) offset = minOffset + (offset - minOffset) / 3;

    this.setOffset(offset, false);
    return true;
  }

  end(x) {
    if (!this.isDown) return;
    this.isDown = false;
    if (this.direction !== 'x') return;

    this.track.classList.remove('is-dragging');
    if (this.pointerId !== null && this.track.releasePointerCapture) {
      try { this.track.releasePointerCapture(this.pointerId); } catch (error) {}
    }

    const deltaX = (typeof x === 'number' ? x : this.lastX) - this.startX;
    const step = this.getStep() || 1;
    let target = this.index;

    if (deltaX < -step * 0.2 || this.velocity < -0.4) target = this.index + 1;
    else if (deltaX > step * 0.2 || this.velocity > 0.4) target = this.index - 1;

    this.goTo(target);

    // The drag ended on a card, so block the click that follows (prevents opening the product link).
    this.suppressClick = true;
    setTimeout(() => { this.suppressClick = false; }, 50);
  }

  onWheel(event) {
    if (Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return;
    event.preventDefault();
    if (this.wheelLocked || Math.abs(event.deltaX) < 10) return;

    this.goTo(this.index + (event.deltaX > 0 ? 1 : -1));
    this.wheelLocked = true;
    setTimeout(() => { this.wheelLocked = false; }, 450);
  }
}
